import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Spinner, Button } from "reactstrap";
import { NOT_IMPLEMENTED_MSG } from "../../../helpers/errorMessages";
import DevNote from "../DevNote";

/**
 * 全站共用的資料狀態元件（主計劃 P0-6）。
 *
 * 對應 DoD 要求的狀態頁：骨架屏 / 載入 / 超時(>10s) / 系統錯誤 / 查無結果。
 * 功能頁不必各自重寫這些狀態，用 <AsyncBoundary> 包住資料區即可。
 */

// —— 骨架屏 ——
export const Skeleton = ({ variant = "table", rows = 5 }) => {
  if (variant === "cards") {
    return (
      <div className="row g-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div className="col-md-4" key={i}>
            <div className="card">
              <div className="card-body">
                <div className="placeholder-glow">
                  <span className="placeholder col-6 mb-2" />
                  <span className="placeholder col-8 mb-2" />
                  <span className="placeholder col-4" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  // 預設：表格列骨架
  return (
    <div className="placeholder-glow">
      {Array.from({ length: rows }).map((_, i) => (
        <div className="d-flex gap-3 py-2 border-bottom" key={i}>
          <span className="placeholder col-1" />
          <span className="placeholder col-3" />
          <span className="placeholder col-3" />
          <span className="placeholder col-2" />
          <span className="placeholder col-2" />
        </div>
      ))}
    </div>
  );
};
Skeleton.propTypes = {
  variant: PropTypes.oneOf(["table", "cards"]),
  rows: PropTypes.number,
};

// —— 載入中 ——
export const Loading = ({ label = "載入中…" }) => (
  <div className="text-center text-muted py-5">
    <Spinner color="primary" className="me-2" />
    <div className="mt-2">{label}</div>
  </div>
);
Loading.propTypes = { label: PropTypes.string };

// —— 查無結果 ——
export const EmptyState = ({
  title = "查無資料",
  hint = "調整查詢條件後再試一次。",
  action = null,
}) => (
  <div className="text-center text-muted py-5">
    <i className="mdi mdi-inbox-outline" style={{ fontSize: 48 }} />
    <h5 className="mt-3 mb-1">{title}</h5>
    <p className="mb-3">{hint}</p>
    {action}
  </div>
);
EmptyState.propTypes = {
  title: PropTypes.string,
  hint: PropTypes.string,
  action: PropTypes.node,
};

// —— 尚未串接正式後端(USE_MOCK=false 的未串接守衛,mock/notImplemented)——
// 不是錯誤:中性呈現、不給重試鈕(重試沒有意義),說得出現況與去路
export const NotConnectedState = () => (
  <div className="text-center text-muted py-5">
    <i className="mdi mdi-lan-disconnect" style={{ fontSize: 48 }} />
    {/* 使用者只看到圖示(這是 USE_MOCK=false 才會到的狀態,部署站走 hybrid 不會出現);說明只給開發者 */}
    <DevNote>
      <h5 className="mt-3 mb-1">尚未串接正式後端</h5>
      <p className="mb-0">此模組的正式 API 尚未串接;開發模式(mock/hybrid)可先預覽完整功能。</p>
    </DevNote>
  </div>
);

// —— 沒有權限(路由層守衛;票 route-guard/01)——
// 確認單的模組三態裡「隱藏」的落點。刻意選「講出來」而不是靜靜導走:
// 導走的話使用者只會覺得「我點了沒反應」,不知道是權限問題還是壞了。
// ⚠️ 會議待確認 Q1:客戶要的「隱藏」若是「連知道模組存在都不該」,改成導走即可 ——
// 兩案共用同一個守衛,差別只有這個元件換掉。
/**
 * 進不去的三種原因要講對,因為**每一種要找的人不一樣**:
 *
 * | 情況 | 文案 | 使用者該去找誰 |
 * |---|---|---|
 * | 這個模組沒開給你 | 沒有這個功能的權限 | 帳號管理者 |
 * | `allHidden`：角色的模組全設為隱藏 | 角色尚未開通任何模組 | 帳號管理者(到「權限設定」開) |
 * | `misconfigured`：一項都沒解析出來 | 權限設定載入異常 | 系統管理者/工程師 |
 *
 * 8/14 實測踩到:testenv 某角色 11 個 module_key 全部認得、access 全是 Hidden,
 * 舊版一律歸類成 `misconfigured` → 畫面說「設定異常」,把人送去找工程師,
 * 但真正該做的是請管理者去開權限。講錯話比不講更浪費時間。
 */
export const NoPermissionState = ({ moduleLabel, misconfigured = false, allHidden = false }) => {
  const kind = misconfigured ? "misconfigured" : allHidden ? "allHidden" : "denied";
  const COPY = {
    misconfigured: {
      icon: "mdi-alert-outline text-warning",
      title: "權限設定載入異常",
      body: "您的角色沒有對應到任何模組權限，這通常是設定問題而非權限不足，請聯繫系統管理者。",
    },
    allHidden: {
      icon: "mdi-lock-outline",
      title: "角色尚未開通任何模組",
      body: "您的角色目前所有模組都設為「隱藏」，因此看不到任何功能。"
        + "請聯繫帳號管理者，於「人員管理 › 權限設定」開啟需要的模組。",
    },
    denied: {
      icon: "mdi-lock-outline",
      title: "沒有這個功能的權限",
      body: `${moduleLabel ? `「${moduleLabel}」` : "這個功能"}需要更高的權限才能使用，請聯繫帳號管理者。`,
    },
  }[kind];
  return (
    <div className="text-center text-muted py-5">
      <i className={`mdi ${COPY.icon}`} style={{ fontSize: 48 }} />
      <h5 className="mt-3 mb-1">{COPY.title}</h5>
      <p className="mb-0">{COPY.body}</p>
    </div>
  );
};
NoPermissionState.propTypes = {
  moduleLabel: PropTypes.string,
  /** true = 一項權限都沒解析出來(對照表沒對上),不是真的沒權限 */
  misconfigured: PropTypes.bool,
  /** true = module_key 都認得,但全部設為 Hidden(資料未開通,不是程式問題) */
  allHidden: PropTypes.bool,
};

// —— 系統錯誤 ——
export const ErrorState = ({
  title = "系統發生錯誤",
  message = "資料載入失敗，請稍後再試。",
  onRetry = null,
}) => {
  // 「尚未串接」不是錯誤,別用紅色錯誤卡+重試鈕嚇人(8/12 使用者回報:
  // 未串接顯示 default 錯誤卡)。在共用出口攔一次,全站頁面同時生效
  if (message === NOT_IMPLEMENTED_MSG) return <NotConnectedState />;
  return (
    <div className="text-center text-muted py-5">
      <i className="mdi mdi-alert-circle-outline text-danger" style={{ fontSize: 48 }} />
      <h5 className="mt-3 mb-1">{title}</h5>
      <p className="mb-3">{typeof message === "string" ? message : "資料載入失敗"}</p>
      {onRetry ? (
        <Button color="primary" outline onClick={onRetry}>
          <i className="mdi mdi-reload me-1" />重試
        </Button>
      ) : null}
    </div>
  );
};
ErrorState.propTypes = {
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onRetry: PropTypes.func,
};

// —— 超時防呆（載入 > 10 秒）——
export const TimeoutHint = ({ onRetry = null }) => (
  <div className="alert alert-warning text-center my-3" role="alert">
    <i className="mdi mdi-clock-alert-outline me-1" />
    資料載入時間較長（已超過 10 秒），可能是網路或伺服器忙碌。
    {onRetry ? (
      <Button color="link" className="p-0 ms-2 align-baseline" onClick={onRetry}>
        重新載入
      </Button>
    ) : null}
  </div>
);
TimeoutHint.propTypes = { onRetry: PropTypes.func };

/**
 * 資料狀態包裝器：依 loading/error/empty 自動切換對應狀態畫面。
 * loading 持續超過 timeoutMs（預設 10s）時，於載入畫面上方顯示超時提示。
 *
 *   <AsyncBoundary loading={isLoading} error={err} isEmpty={rows.length===0}
 *                  onRetry={refetch} skeleton="table">
 *     <DataTable rows={rows} />
 *   </AsyncBoundary>
 */
export const AsyncBoundary = ({
  loading,
  error,
  isEmpty = false,
  onRetry = null,
  skeleton = "table",
  timeoutMs = 10000,
  empty = null,
  children,
}) => {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return undefined;
    }
    const timer = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [loading, timeoutMs]);

  if (loading) {
    return (
      <>
        {timedOut ? <TimeoutHint onRetry={onRetry} /> : null}
        {typeof skeleton === "string" ? (
          <Skeleton variant={skeleton} />
        ) : (
          skeleton || <Loading />
        )}
      </>
    );
  }
  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }
  if (isEmpty) {
    return empty || <EmptyState />;
  }
  return children;
};
AsyncBoundary.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.object]),
  isEmpty: PropTypes.bool,
  onRetry: PropTypes.func,
  skeleton: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  timeoutMs: PropTypes.number,
  empty: PropTypes.node,
  children: PropTypes.node,
};

export default AsyncBoundary;
